import type { LayerSpecification } from 'mapbox-gl';
import { PLAN, UMR } from './tokens';
import type { PlanGeoData } from './types';

export interface PlanLayerVisibility {
  buildings: boolean;
  modified: boolean;
  roads: boolean;
  pipes: boolean;
  perimeter: boolean;
  zone: boolean;
  outside: boolean;
  drift: boolean;
  delivery: boolean;
}

export const DEFAULT_VISIBILITY: PlanLayerVisibility = {
  buildings: true,
  modified: true,
  roads: true,
  pipes: true,
  perimeter: true,
  zone: false,
  outside: false,
  drift: false,
  delivery: true,
};

export const HEAT_STEPS = [
  { label: '< 300 MWh/an', color: UMR.map.consumption[0] },
  { label: '300 – 600 MWh/an', color: UMR.map.consumption[1] },
  { label: '600 – 1 000 MWh/an', color: UMR.map.consumption[2] },
  { label: '> 1 000 MWh/an', color: UMR.map.consumption[3] },
];

const heatColor = ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]];
const op = (on: boolean, value: number) => (on ? value : 0);

/**
 * Layer stack of a Plan project. UMMap only re-applies `paint` and `filter` on updates,
 * so visibility is driven by opacity (never by `layout.visibility`).
 */
export function planLayers(v: PlanLayerVisibility, opts: { pickedId: number | null; scenarioShown: boolean; perimeterOnly?: boolean }): LayerSpecification[] {
  const perimeterOnly = opts.perimeterOnly ?? true;
  return [
    { id: 'plan-zone-fill', type: 'fill', source: 'zone', paint: { 'fill-color': PLAN.map.zone, 'fill-opacity': op(v.zone, 0.1) } },
    { id: 'plan-zone-line', type: 'line', source: 'zone', paint: { 'line-color': PLAN.map.zone, 'line-width': 1.5, 'line-dasharray': [1, 1.5], 'line-opacity': op(v.zone, 0.9) } },
    { id: 'plan-roads', type: 'line', source: 'roads', paint: { 'line-color': PLAN.map.road, 'line-width': 2.5, 'line-opacity': op(v.roads, 0.55) } },
    {
      id: 'plan-building-outside', type: 'fill', source: 'buildings',
      filter: ['==', ['get', 'inside'], 0],
      paint: { 'fill-color': PLAN.map.outside, 'fill-opacity': perimeterOnly ? op(v.outside, 0.55) : op(v.buildings, 0.8) },
    },
    {
      id: 'plan-building-fill', type: 'fill', source: 'buildings',
      filter: ['==', ['get', 'inside'], 1],
      paint: { 'fill-color': heatColor as never, 'fill-opacity': op(v.buildings, 0.85) },
    },
    {
      id: 'plan-building-modified', type: 'line', source: 'buildings',
      filter: ['all', ['==', ['get', 'inside'], 1], ['==', ['get', 'modified'], 1]],
      paint: { 'line-color': PLAN.map.modified, 'line-width': 3, 'line-opacity': op(v.modified && v.buildings, 1) },
    },
    {
      id: 'plan-building-drift', type: 'line', source: 'buildings',
      filter: ['==', ['get', 'drift'], 1],
      paint: { 'line-color': PLAN.map.drift, 'line-width': 2.5, 'line-dasharray': [1.5, 1], 'line-opacity': op(v.drift, 1) },
    },
    {
      id: 'plan-building-scenario', type: 'line', source: 'buildings',
      filter: ['==', ['get', 'connected'], 1],
      paint: { 'line-color': PLAN.map.scenario, 'line-width': 3, 'line-opacity': op(opts.scenarioShown, 1) },
    },
    {
      id: 'plan-building-picked', type: 'line', source: 'buildings',
      filter: ['==', ['get', 'id'], opts.pickedId ?? -1],
      paint: { 'line-color': UMR.map.picked, 'line-width': 3.5 },
    },
    {
      id: 'plan-pipes', type: 'line', source: 'pipes',
      paint: {
        'line-color': ['case', ['==', ['get', 'isEngie'], 1], UMR.map.pipe, PLAN.map.pipeThirdParty] as never,
        'line-width': UMR.map.pipeWidth,
        'line-opacity': op(v.pipes, 1),
      },
    },
    {
      id: 'plan-delivery', type: 'circle', source: 'delivery',
      paint: {
        'circle-color': PLAN.map.delivery,
        'circle-radius': ['interpolate', ['linear'], ['get', 'consumption'], 0, 3, 1000, 5, 5000, 9] as never,
        'circle-stroke-color': PLAN.map.halo,
        'circle-stroke-width': 1,
        'circle-opacity': op(v.delivery, 0.9),
        'circle-stroke-opacity': op(v.delivery, 1),
      },
    },
  ];
}

/** Sources with per-building flags (modified in project, changed in UM since snapshot, connected by scenario). */
export function planSources(
  geo: PlanGeoData,
  flags: { modified: Record<string, number>; drift: Set<string>; connected: Set<string> },
): Record<string, GeoJSON.FeatureCollection> {
  return {
    zone: geo.classificationZone,
    roads: geo.roads,
    pipes: geo.pipes,
    delivery: geo.deliveryPoints,
    perimeter: geo.perimeter,
    buildings: {
      ...geo.buildings,
      features: geo.buildings.features.map((f) => {
        const fid = f.properties?.functionalId as string;
        const override = flags.modified[fid];
        return {
          ...f,
          properties: {
            ...f.properties,
            heat: override ?? f.properties?.heat,
            modified: override !== undefined ? 1 : 0,
            drift: flags.drift.has(fid) ? 1 : 0,
            connected: flags.connected.has(fid) ? 1 : 0,
          },
        };
      }),
    },
  };
}
